import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  buildMyWalletVirtualCardsDownloadFilters,
  buildMyWalletVirtualCardsInitialFilters,
  buildMyWalletVirtualCardsRequestFilters,
} from 'src/app/MyWallet/myWalletVirtualCards.filters'
import {
  selectFilteredVirtualCards,
  selectUsersFullPaginatedDataFromState,
  selectVirtualCardsFullPaginatedData,
} from 'src/app/VirtualCardsYourCards/redux/virtualCardsYourCards.selectors'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import ObjectBoxTitle from 'src/components/ObjectBoxTitle/ObjectBoxTitle'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import VirtualCardsBoxAndLinks, {
  VirtualCardsAnalyticsEventProperties,
  VirtualCardsFiltersBody,
} from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch } from 'src/hooks'
import { translateNode } from 'src/localization/translateNode'
import { myWalletVirtualCardDetailUrl } from 'src/navigation/urls'
import { postReportVirtualCardsDownload } from 'src/redux/actions/data/reportDownloads.actions'
import { getVirtualCards } from 'src/redux/actions/data/virtualCards.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectVirtualCardsActiveRecipientMeCountStatisticsFromState,
  selectVirtualCardsRecipientMeCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const MyWalletVirtualCards: React.FC = () => {
  const virtualCardsFullPaginatedData = useSelector(
    selectVirtualCardsFullPaginatedData,
  )
  const filteredVirtualCards = useSelector(selectFilteredVirtualCards)
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const virtualCardsRecipientMeCount = useSelector(
    selectVirtualCardsRecipientMeCountStatisticsFromState,
  )
  const virtualCardsActiveRecipientMeCount = useSelector(
    selectVirtualCardsActiveRecipientMeCountStatisticsFromState,
  )
  const usersFullPaginatedData = useSelector(
    selectUsersFullPaginatedDataFromState,
  )
  const queryParams = useSelector(selectQueryParams)
  const appName = useSelector(selectAppName)
  const user = useSelector(selectUserListModelFromState)
  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)
  const dispatch = useBackgroundDispatch()
  const { analytics } = useAnalyticsContext()

  const [selectedFilters, setSelectedFilters] = useState<
    VirtualCardsAnalyticsEventProperties | undefined
  >(undefined)
  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)
  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    setClientHeight(viewportHeight.height - 130)
  }, [ref?.current])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 130)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.VirtualCardsRecipientMeCountStatistics,
      appStatisticsKeys.VirtualCardsActiveRecipientMeCountStatistics,
    ])
  }, [dispatchStatistics])

  useEffect(() => {
    if (selectedFilters) {
      analytics?.track('my wallet virtual cards filter applied', {
        status: selectedFilters.status && selectedFilters.status.length > 0,
        search: !!selectedFilters.search,
        card_type:
          selectedFilters.card_type && selectedFilters.card_type.length > 0,
        deactivating_range: !!selectedFilters.deactivating_in,
      })
    }
  }, [analytics, selectedFilters])

  const dispatchPostReportVirtualCardsDownload = useCallback(
    (filters: VirtualCardsFiltersBody): void => {
      dispatch(
        postReportVirtualCardsDownload(
          buildMyWalletVirtualCardsDownloadFilters(filters, user?.id),
        ),
      )
      analytics?.track('my wallet - virtual cards - download my virtual cards')
    },
    [analytics, dispatch, user?.id],
  )

  const fetchVirtualCards = useCallback(
    (filtersBody?: VirtualCardsFiltersBody, page?: number): void => {
      if (user?.id === undefined) {
        return
      }

      dispatch(
        getVirtualCards(
          buildMyWalletVirtualCardsRequestFilters(filtersBody, user.id, page),
        ),
      )
    },
    [dispatch, user?.id],
  )

  const infiniteScrollProps = useMemo(
    () => ({
      ...virtualCardsFullPaginatedData,
      fetch: fetchVirtualCards,
    }),
    [fetchVirtualCards, virtualCardsFullPaginatedData],
  )

  const initialFilterValues = useMemo(
    () => buildMyWalletVirtualCardsInitialFilters(queryParams),
    [queryParams],
  )

  if (
    !statisticsLoaded.virtualCardsRecipientMeCountStatisticsLoaded ||
    !statisticsLoaded.virtualCardsActiveRecipientMeCountStatisticsLoaded
  ) {
    return <LoadingBackgroundPage />
  }

  return (
    <>
      <Helmet>
        <title>{`My Wallet Virtual Cards | ${appName}`}</title>
        <meta
          name="description"
          content={`My Wallet Virtual Cards page of ${appName}`}
        />
      </Helmet>
      <div
        css={css`
          height: 100%;
        `}
        ref={ref}
        id={'ref'}
      >
        <VirtualCardsBoxAndLinks
          onDownloadClick={dispatchPostReportVirtualCardsDownload}
          defaultTitle={
            <ObjectBoxTitle
              static={{
                label: translateNode('Active'),
                count: virtualCardsActiveRecipientMeCount,
              }}
            />
          }
          isYourCards
          virtualCards={filteredVirtualCards}
          emptyStateDesign={TableEmptyStateDesigns.YourVirtualCardsActive}
          infiniteScrollProps={infiniteScrollProps}
          initialFilterValues={initialFilterValues}
          totalCount={virtualCardsRecipientMeCount}
          usersFullPaginatedData={usersFullPaginatedData}
          setSelectedFilters={setSelectedFilters}
          clientHeight={clientHeight || 0}
          getVirtualCardDetailUrl={myWalletVirtualCardDetailUrl}
        />
      </div>
    </>
  )
}

export default MyWalletVirtualCards
